import { For, Show, createSignal, onMount, onCleanup } from "solid-js"
import { ScrollView } from "@opencode-ai/ui/scroll-view"
import { Icon as IconV2 } from "@opencode-ai/ui/v2/icon"
import { FilePathLinkProvider } from "@opencode-ai/session-ui/context"
import type { SkillMarketController } from "./skill-market-controller"
import type { MarketplaceSkill, UploadMetadata, UpdateSkillAttributes, PreparedSkillFile } from "./skill-market-types"
import { SkillMarketCard } from "./skill-market-card"
import { SkillMarketFileViewer } from "./skill-market-file-viewer"
import { SkillMarketUpload } from "./skill-market-upload"
import { SkillMarketAttributes } from "./skill-market-attributes"
import { SkillMarketIcon, SkillGlyph } from "./skill-market-icon"
import "./skill-market.css"

const DETAIL_MIN = 320
const DETAIL_MAX = 640

function basename(path: string) {
  const index = path.lastIndexOf("/")
  return index === -1 ? path : path.slice(index + 1)
}

export function SkillMarketView(props: { controller: SkillMarketController }) {
  const c = props.controller
  const [showVersionModal, setShowVersionModal] = createSignal(false)
  const [versionUpdateSkill, setVersionUpdateSkill] = createSignal<MarketplaceSkill | null>(null)
  const [showAttributesModal, setShowAttributesModal] = createSignal(false)
  const [attributesSkill, setAttributesSkill] = createSignal<MarketplaceSkill | null>(null)
  const [detailWidth, setDetailWidth] = createSignal(380)

  // 无限滚动：观察哨兵元素，进入视口时加载下一页
  let viewport: HTMLDivElement | undefined
  let sentinel: HTMLDivElement | undefined
  let observer: IntersectionObserver | undefined

  onMount(() => {
    if (typeof IntersectionObserver === "undefined") return
    observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) c.loadMore()
        }
      },
      { root: viewport, rootMargin: "200px" },
    )
    if (sentinel) observer.observe(sentinel)
  })
  onCleanup(() => observer?.disconnect())

  // 分类下拉选项（剔除 "全部技能"）
  const uploadCategories = () => c.categories().filter((cat) => cat.code !== "all")

  const totalCount = () => c.categoryCounts()["all"] ?? c.skills().length

  const categoryLabel = (code?: string) => {
    if (!code) return undefined
    return c.categories().find((cat) => cat.code === code)?.label ?? code
  }

  const isInstalled = (skill: MarketplaceSkill) =>
    Boolean(skill.is_installed_global || skill.is_installed_project)

  function startResize(event: PointerEvent) {
    event.preventDefault()
    const startX = event.clientX
    const startWidth = detailWidth()
    const onMove = (moveEvent: PointerEvent) => {
      const next = startWidth - (moveEvent.clientX - startX)
      setDetailWidth(Math.min(DETAIL_MAX, Math.max(DETAIL_MIN, next)))
    }
    const onUp = () => {
      window.removeEventListener("pointermove", onMove)
      window.removeEventListener("pointerup", onUp)
      document.body.style.cursor = ""
    }
    document.body.style.cursor = "col-resize"
    window.addEventListener("pointermove", onMove)
    window.addEventListener("pointerup", onUp)
  }

  function handleVersionUpdate(skill: MarketplaceSkill) {
    setVersionUpdateSkill(skill)
    setShowVersionModal(true)
  }

  function handleAttributes(skill: MarketplaceSkill) {
    setAttributesSkill(skill)
    setShowAttributesModal(true)
  }

  function handleAttributesSubmit(metadata: UpdateSkillAttributes) {
    const target = attributesSkill()
    if (target) {
      c.updateAttributes(target, metadata).then(() => {
        if (!c.error()) {
          setShowAttributesModal(false)
          setAttributesSkill(null)
        }
      })
    }
  }

  function handleVersionUpload(files: PreparedSkillFile[], metadata: UploadMetadata) {
    const target = versionUpdateSkill()
    if (target) {
      c.updateVersion(target, files, metadata).then(() => {
        if (!c.error()) {
          setShowVersionModal(false)
          setVersionUpdateSkill(null)
          // Re-fetch the currently selected file so the right panel shows
          // the updated content immediately instead of the old version.
          const skillId = c.selectedSkillId()
          const filePath = c.selectedFilePath()
          if (skillId && filePath) {
            c.refetchFile(skillId, filePath)
          }
        }
      })
    }
  }

  return (
    <div class="skill-market-root">
      {/* Header */}
      <div class="skill-market-header">
        <div class="skill-market-header-left">
          <h1 class="skill-market-title">技能广场</h1>
          <p class="skill-market-subtitle">
            发现、安装与组合安全技能
            <Show when={totalCount() > 0}>
              <span class="skill-market-count">{totalCount()} 个</span>
            </Show>
          </p>
        </div>
        <div class="skill-market-header-right">
          <div class="skill-market-search">
            <IconV2 name="magnifying-glass" class="skill-market-search-icon" size="small" />
            <input
              type="text"
              placeholder="搜索技能名称、能力或场景..."
              value={c.searchQuery()}
              onInput={(e) => c.setSearchQuery(e.currentTarget.value)}
              class="skill-market-search-input"
            />
          </div>
          <button class="skill-market-upload-btn" onClick={() => c.setShowUpload(true)}>
            上传技能
          </button>
        </div>
      </div>

      <div class="skill-market-body">
        {/* Left rail - categories (collapsed to glyphs, expands on hover) */}
        <div class="skill-market-rail">
          <div class="skill-market-rail-panel">
            <For each={c.categories()}>
              {(cat) => (
                <button
                  class="skill-market-rail-btn"
                  classList={{ "skill-market-rail-btn--active": c.selectedCategory() === cat.code }}
                  onClick={() => c.setSelectedCategory(cat.code)}
                  title={cat.label}
                >
                  <span class="skill-market-rail-icon">
                    <SkillGlyph code={cat.code} size={18} />
                  </span>
                  <span class="skill-market-rail-label">{cat.label}</span>
                  <Show when={c.categoryCounts()[cat.code]}>
                    <span class="skill-market-rail-count">{c.categoryCounts()[cat.code]}</span>
                  </Show>
                </button>
              )}
            </For>
          </div>
        </div>

        {/* Center - skill cards */}
        <div class="skill-market-center">
          <ScrollView viewportRef={(el) => (viewport = el)}>
            <div class="skill-market-grid">
              <For each={c.skills()}>
                {(skill) => (
                  <SkillMarketCard
                    skill={skill}
                    selected={c.selectedSkillId() === skill.id}
                    onSelect={() => c.selectSkill(skill.id)}
                    onInstall={() => c.installSkill(skill, "global")}
                  />
                )}
              </For>
            </div>
            <Show when={c.skills().length === 0 && !c.loadingMore() && !c.refreshing()}>
              <div class="skill-market-empty">
                <span class="skill-market-empty-title">暂无技能</span>
                <span class="skill-market-empty-hint">切换到其他分类，或上传一个新的技能</span>
              </div>
            </Show>
            {/* 无限滚动哨兵 + 底部状态 */}
            <div ref={sentinel} class="skill-market-list-sentinel">
              <Show when={c.refreshing() || c.loadingMore()}>
                <span class="skill-market-list-status">加载中...</span>
              </Show>
              <Show when={!c.hasMore() && c.skills().length > 0}>
                <span class="skill-market-list-status">共 {c.skills().length} 个技能</span>
              </Show>
            </div>
          </ScrollView>
        </div>

        {/* Right - skill detail: identity + actions + files + content */}
        <div class="skill-market-detail-panel" style={{ width: `${detailWidth()}px` }}>
          <div class="skill-market-resizer" onPointerDown={startResize} title="拖动调整宽度" />
          <Show
            when={c.selectedSkill()}
            fallback={<div class="skill-market-detail-empty">选择一个技能查看详情</div>}
          >
            {(skill) => (
              <>
                <div class="skill-market-detail-head">
                  <div class="skill-market-detail-head-row">
                    <SkillMarketIcon skill={skill()} size={38} />
                    <div class="skill-market-detail-headtext">
                      <div class="skill-market-detail-name-row">
                        <span class="skill-market-detail-name">
                          {skill().display_name ?? skill().name}
                        </span>
                        <Show when={isInstalled(skill())}>
                          <span class="skill-market-detail-installed">已安装</span>
                        </Show>
                      </div>
                      <div class="skill-market-detail-meta">
                        <Show when={categoryLabel(skill().category)}>
                          <span>{categoryLabel(skill().category)}</span>
                          <span class="skill-market-detail-meta-dot">·</span>
                        </Show>
                        <span>v{skill().version ?? "1.0.0"}</span>
                        <Show when={skill().author}>
                          <span class="skill-market-detail-meta-dot">·</span>
                          <span>{skill().author}</span>
                        </Show>
                      </div>
                    </div>
                    <button
                      class="skill-market-drawer-close"
                      onClick={() => c.deselectSkill()}
                      title="关闭详情"
                    >
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                        <path
                          d="M2.5 2.5l7 7M9.5 2.5l-7 7"
                          stroke="currentColor"
                          stroke-width="1.4"
                          stroke-linecap="round"
                        />
                      </svg>
                    </button>
                  </div>

                  <div class="skill-market-detail-actions">
                    <Show
                      when={!isInstalled(skill())}
                      fallback={
                        <>
                          <button
                            class="skill-market-detail-btn-ghost"
                            disabled={c.installing()}
                            onClick={() => c.uninstallSkill(skill(), "global")}
                          >
                            卸载全局
                          </button>
                          <button
                            class="skill-market-detail-btn-ghost"
                            disabled={c.installing()}
                            onClick={() => c.uninstallSkill(skill(), "project")}
                          >
                            卸载项目
                          </button>
                        </>
                      }
                    >
                      <button
                        class="skill-market-detail-btn-primary"
                        disabled={c.installing()}
                        onClick={() => c.installSkill(skill(), "global")}
                      >
                        {c.installing() ? "安装中..." : "安装到全局"}
                      </button>
                      <button
                        class="skill-market-detail-btn-secondary"
                        disabled={c.installing()}
                        onClick={() => c.installSkill(skill(), "project")}
                      >
                        安装到项目
                      </button>
                    </Show>
                    <button
                      class="skill-market-detail-btn-ghost"
                      onClick={() => handleVersionUpdate(skill())}
                      title="更新版本"
                    >
                      更新
                    </button>
                    <button
                      class="skill-market-detail-btn-ghost"
                      onClick={() => handleAttributes(skill())}
                      title="技能属性"
                    >
                      属性
                    </button>
                    <button
                      class="skill-market-detail-btn-danger-ghost"
                      onClick={() => {
                        if (
                          confirm(
                            `确定要删除「${skill().display_name ?? skill().name}」吗？此操作不可撤销。`,
                          )
                        ) {
                          c.removeSkill(skill())
                        }
                      }}
                    >
                      删除
                    </button>
                  </div>

                  <Show when={(skill().file_manifest?.length ?? 0) > 0}>
                    <div class="skill-market-detail-files">
                      <For each={skill().file_manifest ?? []}>
                        {(file) => (
                          <button
                            class="skill-market-file-chip"
                            classList={{
                              "skill-market-file-chip--active": c.selectedFilePath() === file,
                            }}
                            onClick={() => c.selectFile(file)}
                            title={file}
                          >
                            {basename(file)}
                          </button>
                        )}
                      </For>
                    </div>
                  </Show>
                </div>

                <FilePathLinkProvider directory="" onFocus={() => {}}>
                  <SkillMarketFileViewer
                    skill={c.selectedSkill()}
                    filePath={c.selectedFilePath()}
                    fileContent={c.fileContent()}
                    loading={c.fileLoading()}
                  />
                </FilePathLinkProvider>
              </>
            )}
          </Show>
        </div>
      </div>

      {/* Error toast */}
      <Show when={c.error()}>
        <div class="skill-market-error-toast">{c.error()}</div>
      </Show>

      {/* Upload modal */}
      <SkillMarketUpload
        show={c.showUpload()}
        uploading={c.uploading()}
        categories={uploadCategories()}
        onCreateCategory={c.createCategory}
        creatingCategory={c.creatingCategory()}
        onClose={() => c.setShowUpload(false)}
        onUpload={(files, metadata) => {
          c.uploadSkill(files, metadata).then(() => {
            if (!c.error()) c.setShowUpload(false)
          })
        }}
      />

      {/* Version update modal */}
      <SkillMarketUpload
        show={showVersionModal()}
        uploading={c.uploading()}
        categories={uploadCategories()}
        onCreateCategory={c.createCategory}
        creatingCategory={c.creatingCategory()}
        onClose={() => {
          setShowVersionModal(false)
          setVersionUpdateSkill(null)
        }}
        onUpload={handleVersionUpload}
        title="更新技能版本"
        prefillSkill={versionUpdateSkill()}
      />

      {/* Skill attributes (metadata-only) modal */}
      <SkillMarketAttributes
        show={showAttributesModal()}
        saving={c.uploading()}
        categories={uploadCategories()}
        onCreateCategory={c.createCategory}
        creatingCategory={c.creatingCategory()}
        onClose={() => {
          setShowAttributesModal(false)
          setAttributesSkill(null)
        }}
        onSubmit={handleAttributesSubmit}
        prefillSkill={attributesSkill()}
      />
    </div>
  )
}
